import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, Activity } from '@/lib/api';
import { ActivityType } from '@/types/activity';
import {
  CheckCircle,
  MessageSquare,
  AlertCircle,
  AlertTriangle,
  FileText,
  UserPlus,
  Clock,
  TrendingUp,
  X,
  User,
  Calendar,
  Send,
  Eye,
  IndianRupee,
  Mail,
  Ban
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigate } from 'react-router-dom';

// Map activity types to icons and colors. Note: ActivityType is a broad union —
// keys not present here fall back to the default (FileText / gray) at render time.
const activityConfig: Partial<Record<ActivityType, { icon: React.ElementType, colorClass: string }>> = {
  // Lead lifecycle
  LEAD_CREATED: { icon: UserPlus, colorClass: 'bg-blue-50 text-blue-600' },
  LEAD_UPDATED: { icon: FileText, colorClass: 'bg-gray-50 text-gray-600' },
  LEAD_STAGE_CHANGED: { icon: TrendingUp, colorClass: 'bg-purple-50 text-purple-600' },
  STAGE_CHANGE: { icon: TrendingUp, colorClass: 'bg-purple-50 text-purple-600' },
  LEAD_CLOSED_WON: { icon: CheckCircle, colorClass: 'bg-green-50 text-green-600' },
  LEAD_CLOSED_LOST: { icon: X, colorClass: 'bg-red-50 text-red-600' },
  LEAD_REASSIGNED: { icon: UserPlus, colorClass: 'bg-blue-50 text-blue-600' },
  LEAD_ASSIGNED: { icon: UserPlus, colorClass: 'bg-blue-50 text-blue-600' },
  // Communication
  WHATSAPP_SENT: { icon: MessageSquare, colorClass: 'bg-blue-50 text-blue-600' },
  WHATSAPP_RECEIVED: { icon: MessageSquare, colorClass: 'bg-green-50 text-green-600' },
  // Tasks & notes
  TASK_CREATED: { icon: FileText, colorClass: 'bg-orange-50 text-orange-600' },
  TASK_COMPLETED: { icon: CheckCircle, colorClass: 'bg-green-50 text-green-600' },
  NOTE_ADDED: { icon: FileText, colorClass: 'bg-gray-50 text-gray-600' },
  FOLLOWUP_SCHEDULED: { icon: Clock, colorClass: 'bg-orange-50 text-orange-600' },
  DEAL_VALUE_UPDATED: { icon: TrendingUp, colorClass: 'bg-green-50 text-green-600' },
  PRIORITY_CHANGED: { icon: AlertCircle, colorClass: 'bg-yellow-50 text-yellow-600' },
  // Invoice lifecycle (Wave 4 — W4-D1)
  INVOICE_CREATED: { icon: FileText, colorClass: 'bg-blue-50 text-blue-600' },
  INVOICE_SENT: { icon: Send, colorClass: 'bg-blue-50 text-blue-600' },
  INVOICE_VIEWED: { icon: Eye, colorClass: 'bg-indigo-50 text-indigo-600' },
  INVOICE_PAID: { icon: CheckCircle, colorClass: 'bg-green-50 text-green-600' },
  INVOICE_CANCELLED: { icon: Ban, colorClass: 'bg-red-50 text-red-600' },
  INVOICE_OVERDUE: { icon: AlertTriangle, colorClass: 'bg-amber-50 text-amber-600' },
  // Payment
  PAYMENT_RECORDED: { icon: IndianRupee, colorClass: 'bg-green-50 text-green-600' },
  // Email
  EMAIL_SENT: { icon: Mail, colorClass: 'bg-gray-50 text-gray-600' },
  EMAIL_BOUNCED: { icon: AlertCircle, colorClass: 'bg-red-50 text-red-600' },
};

export const RecentActivityFeed: React.FC = () => {
  const navigate = useNavigate();

  const { data: activities, isLoading, error } = useQuery({
    queryKey: ['recentActivities'],
    queryFn: () => api.getRecentActivities(10),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const handleActivityClick = (activity: Activity) => {
    // Prefer invoice link for invoice-lifecycle events so users jump straight
    // to the invoice detail instead of the parent lead.
    const invoiceId = activity.metadata?.invoiceId;
    if (invoiceId && typeof activity.type === 'string' && activity.type.startsWith('INVOICE_')) {
      navigate(`/invoices/${invoiceId}`);
      return;
    }

    // Navigate to related lead/task if available
    if (activity.relatedLead) {
      navigate(`/leads/${activity.relatedLead.id}`);
    } else if (activity.relatedTask) {
      navigate('/tasks');
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex items-center gap-3 p-2 rounded-lg animate-pulse bg-gray-100">
                <div className="h-4 w-4 bg-gray-300 rounded"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-300 rounded w-3/4 mb-1"></div>
                  <div className="h-3 bg-gray-300 rounded w-1/4"></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8">
            <AlertCircle className="h-12 w-12 text-red-500 mb-3" />
            <p className="text-sm text-red-600">Failed to load recent activities</p>
            <p className="text-xs text-gray-500 mt-1">
              {error instanceof Error ? error.message : 'Unknown error occurred'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8">
            <Clock className="h-12 w-12 text-gray-400 mb-3" />
            <p className="text-sm text-gray-500">No recent activity to display</p>
            <p className="text-xs text-gray-400 mt-1">Activities will appear here as you work</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Recent Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {activities.map((activity) => {
            const config = activityConfig[activity.type] || {
              icon: FileText,
              colorClass: 'bg-gray-50 text-gray-600'
            };
            const Icon = config.icon;
            const hasInvoiceLink = !!activity.metadata?.invoiceId;
            const isClickable = !!activity.relatedLead || !!activity.relatedTask || hasInvoiceLink;

            return (
              <div
                key={activity.id}
                className={`flex items-center gap-3 p-2 rounded-lg ${config.colorClass} ${
                  isClickable
                    ? 'cursor-pointer hover:opacity-80 transition-opacity'
                    : ''
                }`}
                onClick={() => handleActivityClick(activity)}
                role={isClickable ? 'button' : undefined}
                tabIndex={isClickable ? 0 : undefined}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{activity.description}</p>
                  {activity.user && activity.user.name !== 'System' && (
                    <p className="text-xs text-gray-600 mt-0.5 truncate">
                      by {activity.user.name}
                    </p>
                  )}
                  <p className="text-xs text-gray-500" title={activity.createdAt ? new Date(activity.createdAt).toLocaleString() : ''}>
                    {activity.relativeTime}
                    {activity.createdAt && (
                      <span className="ml-1 text-gray-400">
                        • {new Date(activity.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
